import axios from 'axios';
import PropTypes from 'prop-types';
import { createContext, FC, ReactNode, useEffect, useMemo, useState } from 'react';
import avCorpLogo from '../assets/img/logo192.png';
import Avatar from '../components/Avatar';
import Spinner from '../components/bootstrap/Spinner';
import Page from '../layout/Page/Page';
import { IUserAdmins } from '../types/user-type';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
const LOGIN_URL = process.env.REACT_APP_LOGIN_URL || 'http://localhost:3000/login';

export interface IAuthContextProps {
	user: string;
	setUser(user: string): void;
	userData: Partial<IUserAdmins>;
	setUserData(data: Partial<IUserAdmins>): void;
	jwtToken: string;
	setJwtToken(token: string): void;
	csrfToken: string;
	setCsrfToken(token: string): void;
	isSessionActive: boolean;
	setIsSessionActive(active: boolean): void;
	logout(): void;
}

const AuthContext = createContext<IAuthContextProps>({} as IAuthContextProps);

interface IAuthContextProviderProps {
	children: ReactNode;
}

export const AuthContextProvider: FC<IAuthContextProviderProps> = ({ children }) => {
	const [user, setUser] = useState<string>(localStorage.getItem('facit_authUsername') || '');
	const [userData, setUserData] = useState<Partial<IUserAdmins>>({});
	const [jwtToken, setJwtToken] = useState<string>(localStorage.getItem('ay_token') || '');
	const [csrfToken, setCsrfToken] = useState<string>('');
	const [isSessionActive, setIsSessionActive] = useState<boolean>(true);
	const [readyToRender, setReadyToRender] = useState<boolean>(false);

	useEffect(() => {
		localStorage.setItem('facit_authUsername', user);
	}, [user]);

	useEffect(() => {
		localStorage.setItem('ay_token', jwtToken);
	}, [jwtToken]);

	const logout = () => {
		localStorage.removeItem('ay_token');
		setJwtToken('');
		setUserData({});
		window.location.href = LOGIN_URL;
	};

	useEffect(() => {
		const token = localStorage.getItem('ay_token');
		if (!token) {
			window.location.href = LOGIN_URL;
			return;
		}

		const fetchUser = async () => {
			try {
				const response = await axios.get(`${API_BASE}/me`, {
					headers: { Authorization: `Bearer ${token}` },
				});
				setJwtToken(token);
				setUserData(response.data);
				setUser(response.data?.name || response.data?.email || '');
				setReadyToRender(true);
			} catch (_err) {
				localStorage.removeItem('ay_token');
				setJwtToken('');
				setUserData({});
				window.location.href = LOGIN_URL;
			}
		};

		fetchUser();
	}, []);

	const value = useMemo(
		() => ({
			user,
			setUser,
			userData,
			setUserData,
			jwtToken,
			setJwtToken,
			isSessionActive,
			setIsSessionActive,
			csrfToken,
			setCsrfToken,
			logout,
		}),
		[user, userData, isSessionActive, jwtToken, csrfToken],
	);

	return (
		<AuthContext.Provider value={value}>
			{readyToRender ? (
				children
			) : (
				<Page>
					<div className='d-flex align-items-center justify-content-center vh-100'>
						<div className='position-relative d-inline-flex align-items-center justify-content-center'>
							<Spinner color='dark' isGrow={false} size={200} />
							<Avatar
								className='position-absolute'
								style={{
									top: '50%',
									left: '50%',
									transform: 'translate(-50%, -50%)',
								}}
								src={avCorpLogo}
								size={190}
								color='info'
							/>
						</div>
					</div>
				</Page>
			)}
		</AuthContext.Provider>
	);
};

AuthContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default AuthContext;
